import './navigation-template.scss';

import React, { useEffect, useCallback, useState, useMemo, useRef, useContext } from 'react';
import { MenuOpenState, SideNavigationMenuProps, MenuStatus } from '../../types';
import { useScreenSize } from '../../utils/media-query';
import { navigation } from '../../app-navigation';
import { useNavigation } from '../../contexts/navigation';
import TreeView, { TreeViewRef } from 'devextreme-react/cjs/tree-view';
import * as events from 'devextreme/events';
import { useUserSessionContext } from '../../contexts/UserSessionContext';

export type NavigationItem = {
    path: string;
    text: string;
    icon: string;
    feature: string;
    items?: NavigationItem[];
}

export const NavigationTemplate = (props: React.PropsWithChildren<SideNavigationMenuProps>) => {
    const { children, selectedItemChanged, openMenu, compactMode, onMenuReady, menuStatus } = props;
    const { user } = useUserSessionContext();
    const { isLarge } = useScreenSize();
    const treeViewRef = useRef<TreeViewRef>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const items: NavigationItem[] = useMemo(() => {
        const lowerFeatures = user?.features?.map((f: string) => f.toLowerCase()) ?? [];

        const filterByFeature = (items: NavigationItem[]): NavigationItem[] => {
            return items
                .map(item => {
                    if ((item.items ?? []).length > 0) {
                        const filteredChildren = filterByFeature(item.items!);

                        if (filteredChildren.length > 0) {
                            return {
                                ...item,
                                path: item.path && !item.path.startsWith("/") ? `/${item.path}` : item.path,
                                items: filteredChildren
                            }
                        }
                    } else {
                        const hasAccess = !item.feature || lowerFeatures.includes(item.feature.toLowerCase());

                        if (hasAccess) {
                            return {
                                ...item,
                                path: item.path && !item.path.startsWith("/") ? `/${item.path}` : item.path,
                                items: []
                            };
                        }
                    }

                    return null;
                })
                .filter((i) => i !== null) as NavigationItem[];
        };

        return filterByFeature(navigation);
    }, [navigation, isLarge, menuStatus, user?.features]);

    const {
        navigationData: { currentPath }
    } = useNavigation();

    const getWrapperRef = useCallback(
        (element) => {
            const prevElement = wrapperRef.current;
            if (prevElement)
                events.off(prevElement, 'dxclick');

            wrapperRef.current = element;
            events.on(element, 'dxclick', (e: React.PointerEvent) => {
                openMenu(e);
            });
        },
        [openMenu]
    );

    useEffect(() => {
        const treeView = treeViewRef.current && treeViewRef.current.instance();
        if (!treeView)
            return;

        if (currentPath !== undefined)
            treeView.expandItem(currentPath);

        if (compactMode)
            treeView.collapseAll();
    }, [currentPath, compactMode]);

    return (
        <div className={`dx-swatch-additional side-navigation-menu`} ref={getWrapperRef}>
            {children}
            <div className='menu-container theme-dependent'>
                <TreeView 
                    ref={treeViewRef}
                    items={items}
                    keyExpr='path'
                    selectionMode='single'
                    focusStateEnabled={false}
                    expandEvent='click'
                    onItemClick={selectedItemChanged}
                    onContentReady={onMenuReady}
                    width='100%'
                    itemRender={(item: NavigationItem) => (
                        <React.Fragment>
                            <i className='dx-icon material-symbols-outlined'>{item.icon}</i>
                            {(menuStatus === MenuOpenState.Opened) && <span>{item.text}</span>}
                        </React.Fragment>
                    )}
                />
            </div>
        </div>
    )
}